import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import get from 'lodash/get';
import {GatewayGetSchema} from '../../../api/DBSchema/Gateway';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, isAxiosAuthError, isStringI18n, extractI18nString, doSeamlessLogin, hasValue, isAxiosTimeoutError} from '../../../utils/utils';
import {UserGetSchema} from '../../../api/DBSchema/User';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiActions} from '../../../api/consts';
import lockedIcon from '../../../assets/images/locked.png';
import unlockedIcon from '../../../assets/images/unlocked.png';
import store from '../../../store';
import {push} from 'connected-react-router';
import {SMCPaths} from '../../../conf/Routes';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {GatewayHAStatuses} from '../../../api/DBSchema/Gateway';
import GatewayGeneral from './InnerPages/GatewayGeneral';
import GatewayLocation from './InnerPages/GatewayLocation';
import GatewayOwner from './InnerPages/GatewayOwner';
import GatewayStatus from './InnerPages/GatewayStatus';
import GatewayInternetMonitoring from './InnerPages/GatewayInternetMonitoring';
import GatewayNtp from './InnerPages/GatewayNtp';
import GatewayTimezone from './InnerPages/GatewayTimezone';
import GatewayFirewall from './InnerPages/GatewayFirewall';
import GatewayDns from './InnerPages/GatewayDns';
import GatewayAdministrators from './InnerPages/GatewayAdministrators';
import GatewayAccessPolicy from './InnerPages/GatewayAccessPolicy';
import GatewayThreatPrevention from './InnerPages/GatewayThreatPrevention';
import GatewayUnifiedThreatPrevention from './InnerPages/GatewayUnifiedThreatPrevention';
import GatewayThreatPreventionExceptions from './InnerPages/GatewayThreatPreventionExceptions';
import GatewayAntiSpam from './InnerPages/GatewayAntiSpam';
import GatewayQos from './InnerPages/GatewayQos';
import GatewayRemoteAccess from './InnerPages/GatewayRemoteAccess';
import GatewaySiteToSiteVpn from './InnerPages/GatewaySiteToSiteVpn';
import GatewayUserAwareness from './InnerPages/GatewayUserAwareness';
import GatewayAntiVirus from './InnerPages/GatewayAntiVirus';
import GatewayAntiBot from './InnerPages/GatewayAntiBot';
import GatewayPeriodicBackup from './InnerPages/GatewayPeriodicBackup';
import GatewayReports from './InnerPages/GatewayReports';
import GatewayThreatEmulation from './InnerPages/GatewayThreatEmulation';
import GatewayManagedServices from './InnerPages/GatewayManagedServices';
import GatewaySSLInspection from './InnerPages/GatewaySSLInspection';
import GatewayReportRecipients from './InnerPages/GatewayReportRecipients';
import GatewayNotifications from './InnerPages/GatewayNotifications';
import GatewayAdministratorAccess from './InnerPages/GatewayAdministratorAccess';
import GatewayGatewaysBehindNat from './InnerPages/GatewayGatewaysBehindNat';
import GatewayGatewayBladesLicenses from './InnerPages/GatewayGatewayBladesLicenses';
import GatewayNotificationsRecipients from './InnerPages/GatewayNotificationsRecipients';
import haOkLogo from '../../../assets/images/ha_ok.png';
import haWarningLogo from '../../../assets/images/ha_warning.png';
import haErrorLogo from '../../../assets/images/ha_error.png';
import standbyIcon from '../../../assets/images/standby.png';
import inactiveIcon from '../../../assets/images/inactive.png';
import generalIcon from '../../../assets/images/general.png';
import locationIcon from '../../../assets/images/location.png';
import ownerIcon from '../../../assets/images/owner.png';
import administratorIcon from '../../../assets/images/administrator.svg';
import exceptionsIcon from '../../../assets/images/exceptions.png';
import {connectedIcon, disconnectedIcon, disabledIcon} from '../../../assets/images/gateway';
import deviceSettingsIcon from '../../../assets/images/device-settings.png';
import ntpIcon from '../../../assets/images/ntp.png';
import timezoneIcon from '../../../assets/images/timezone.png';
import dnsIcon from '../../../assets/images/dns.png';
import securityIcon from '../../../assets/images/security.png';
import firewallIcon from '../../../assets/images/firewall_16.png';
import fwRulesIcon from '../../../assets/images/fw_rules.png';
import appControlIcon from '../../../assets/images/app_control_16.png';
import ipsIcon from '../../../assets/images/ips_16.png';
import antiVirusIcon from '../../../assets/images/anti_virus_16.png';
import antiSpamIcon from '../../../assets/images/anti_spam_16.png';
import qosIcon from '../../../assets/images/qos_16.png';
import vpnRemoteIcon from '../../../assets/images/vpn_remote_16.png';
import vpnSiteIcon from '../../../assets/images/vpn_site_16.png';
import idaIcon from '../../../assets/images/ida_16.png';
import antiBotIcon from '../../../assets/images/anti_bot_16.png';
import threatEmulationIcon from '../../../assets/images/threat_emulation_16.png';
import policyIcon from '../../../assets/images/policy.png';
import sslInspectionIcon from '../../../assets/images/ssl_inspection_16.png';
import servicesIcon from '../../../assets/images/services.png';
import settingsIcon from '../../../assets/images/settings.png';
import vpnIcon from '../../../assets/images/vpn_site_16.png';
import communityIcon from '../../../assets/images/community.png';
import licenseIcon from '../../../assets/images/license.png';
import internetIcon from '../../../assets/images/internet-small.png';
import topologyIcon from '../../../assets/images/topology.png';
import updatesIcon from '../../../assets/images/updates.png';
import backupIcon from '../../../assets/images/backup.png';
import reportsIcon from '../../../assets/images/reports.png';
import administratorAccessIcon from '../../../assets/images/administrator_access_16.png';
import gatewaysBehindNatIcon from '../../../assets/images/gateways_behind_nat_16.png';
import scriptIcon from '../../../assets/images/script.png';
import pageIcon from '../../../assets/images/page.png';
import clipboardIcon from '../../../assets/images/clipboard.png';
import GatewayCliScript1 from './InnerPages/GatewayCliScipt1';
import GatewayCliScript2 from './InnerPages/GatewayCliScipt2';
import GatewayCliScript3 from './InnerPages/GatewayCliScipt3';
import GatewayCliScript4 from './InnerPages/GatewayCliScipt4';
import GatewayCliScript5 from './InnerPages/GatewayCliScipt5';
import GatewayCliScriptFromApi from './InnerPages/GatewayCliSciptFromApi';
import GatewayApplicationsAndUrls from './InnerPages/GatewayApplicationsAndUrls';
import GatewayIps from './InnerPages/GatewayIps';
import GatewayTraditionalAntiVirus from './InnerPages/GatewayTraditionalAntiVirus';
import GatewayDynamicDns from './InnerPages/GatewayDynamicDns';
import GatewayFirmware from './InnerPages/GatewayFirmware';
import GatewayCustomFields from './InnerPages/GatewayCustomFields';
import GatewayVpnCommunity from './InnerPages/GatewayVpnCommunity';
import GatewayVpnAuthenticationMethod from './InnerPages/GatewayVpnAuthenticationMethod';
import GatewayVpnInternalNetworkTopology from './InnerPages/GatewayVpnInternalNetworkTopology';
import GatewayLogs from './InnerPages/GatewayLogs';
import {useSelector} from 'react-redux';
import {isSMELogsEnabledSelector, userPermissionsSelector} from '../../../store/selectors/user.selectors';
import GatewayHaStatus from './InnerPages/GatewayHaStatus';
import GatewayHaMemberStatus from './InnerPages/GatewayHaMemberStatus';
import {helpContextPageUrl} from '../../../conf/Routes';
import GatewayLocalUsers from "./InnerPages/GatewayLocalUsers";


export function isPageLockedToPlan(gatewayObject, pageName) {
    return gatewayObject[pageName][GatewayGetSchema.shared.lockedToPlan];
}

export function getPageData(editorObject, pageName) {
    return isPageLockedToPlan(editorObject, pageName) ? editorObject.plan[pageName] : editorObject.gateway[pageName];
}

export function useGatewayEditor(gatewayId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const isSMELogsEnabled = useSelector(isSMELogsEnabledSelector);
    const userPermissions = useSelector(userPermissionsSelector);

    const [origGateway, setOrigGateway] = useState({});
    const [origPlan, setOrigPlan] = useState({});
    const [origUser, setOrigUser] = useState({});
    const [gatewayObject, setGatewayObject] = useState({});
    const [planObject, setPlanObject] = useState({});
    const [userObject, setUserObject] = useState({});
    const [errorReadingUserObject, setErrorReadingUserObject] = useState(false);
    const [availableFirmwares, setAvailableFirmwares] = useState({});
    const [isSMEReportEnabled, setIsSMEReportEnabled] = useState(false);
    const [isReportConfirmationEnabled, setIsReportConfirmationEnabled] = useState(false);
    const [isHasEditPermissionForReportsConfig, setIsHasEditPermissionForReportsConfig] = useState(false);
    
    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [appiData, setAppiData] = useState([]);

    const [uoTableData, setUoData] = useState([]);

    const [zonesData, setZonesData] = useState([]);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const setAppiDataValue = function(appiDataValue){
        if(appiData.length === 0 && appiDataValue!==null){
            setAppiData(appiDataValue);
        }
    }
    const setUoTableDataValue = function(uoTableDataValue){
        if(uoTableData.length === 0 && uoTableDataValue!==null){
            setUoData(uoTableDataValue);
        }
    }

    // Is gateway external (not managed in SMP)
    const isExternal = useMemo(() => {
        return !get(gatewayObject, [GatewayGetSchema.pages.general.pageId, GatewayGetSchema.pages.general.managedBySmp]);
    }, [gatewayObject]);

    // Is gateway member of cluster
    const isMemberOfCluster = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.memberOfCluster]);
    }, [gatewayObject]);

    const gatewayStatusIcon = useMemo(() => {
        const gatewayConnectionStatus = get(gatewayObject, [GatewayGetSchema.pages.status.pageId, GatewayGetSchema.pages.status.connectionStatus]);
        switch (gatewayConnectionStatus) {
            case 'SMP.GATEWAYS.PAGE.STATUS.NOT_CONNECTED':
                return disconnectedIcon;
            case 'SMP.GATEWAYS.PAGE.STATUS.CONNECTED':
                return connectedIcon;
            case 'SMP.GATEWAYS.PAGE.STATUS.DISABLED':
            case 'SMP.GATEWAYS.PAGE.STATUS.NOT_MANAGED_BY_SMP':
                return disabledIcon;
            default:
                return undefined;
        }
    }, [gatewayObject]);

    const gatewayHAStatusIcon = useMemo(() => {
        const gatewayHAStatus = get(gatewayObject, [GatewayGetSchema.pages.haStatus.pageId, GatewayGetSchema.pages.haStatus.state]);
        switch (gatewayHAStatus) {
            case GatewayHAStatuses.ok:
                return haOkLogo;
            case GatewayHAStatuses.warning:
                return haWarningLogo;
            case GatewayHAStatuses.error:
                return haErrorLogo;
            default:
                return undefined;
        }
    }, [gatewayObject]);

    const gatewayHAMemberStatusIcon = useMemo(() => {
        const gatewayHAMemberStatus = get(gatewayObject, [GatewayGetSchema.pages.haMemberStatus.pageId, GatewayGetSchema.pages.haMemberStatus.state]);
        switch (gatewayHAMemberStatus) {
            case GatewayGetSchema.pages.haMemberStatus.stateValues.unknown:
                return disabledIcon;
            case GatewayGetSchema.pages.haMemberStatus.stateValues.standby:
                return standbyIcon;
            case GatewayGetSchema.pages.haMemberStatus.stateValues.active:
                return connectedIcon;
            case GatewayGetSchema.pages.haMemberStatus.stateValues.inactive:
                return inactiveIcon;
            case GatewayGetSchema.pages.haMemberStatus.stateValues.down:
                return disconnectedIcon;
            default:
                return undefined;
        }
    }, [gatewayObject]);

    // Does gateway have custom field page in editor
    const hasCustomFieldPage = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.pages.customFields.pageId, GatewayGetSchema.pages.customFields.customFieldOne]);
    }, [gatewayObject]);

    const hasHaStatusPage = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.pages.haStatus.pageId]);
    }, [gatewayObject]);

    const hasHaMemberStatusPage = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.pages.haMemberStatus.pageId]);
    }, [gatewayObject]);

    const hasIpAddress = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.pages.general.pageId, GatewayGetSchema.pages.general.lastConnectedIp]);
    }, [gatewayObject]);

    const isReachMyDeviceEnabled = useMemo(() => {
        return !!get(gatewayObject, [GatewayGetSchema.pages.gatewaysBehindNAT.pageId, GatewayGetSchema.pages.gatewaysBehindNAT.accessServiceUrl]);
    }, [gatewayObject]);

    const getGatewayPageExtraIcon = useCallback((pageName) => {
        if (!isPageDataLoaded || !gatewayObject[pageName]) {
            return undefined;
        }
        // If gateway is external, don't show any icon
        if (isExternal) {
            return undefined;
        }
        switch (gatewayObject[pageName][GatewayGetSchema.shared.lockedToPlan]) {
            case true:
                return lockedIcon;
            case false:
                    return unlockedIcon;
            default:
                return undefined;
        }
    }, [gatewayObject, isPageDataLoaded, isExternal]);

    const editorPages = useMemo(() => (
        [
            { id: GatewayGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: GatewayGeneral, helpId: helpContextPageUrl.gatewayGeneral },
            { id: GatewayGetSchema.pages.location.pageId, title: t('SMP.GATEWAYS.INNER_MENU.LOCATION'), icon: locationIcon, pageElement: GatewayLocation, helpId: helpContextPageUrl.gatewayLocation },            
            { id: GatewayGetSchema.pages.owner.pageId, title: t('SMP.GATEWAYS.INNER_MENU.OWNER'), icon: ownerIcon, pageElement: GatewayOwner, helpId: helpContextPageUrl.gatewayOwner, hide: !userPermissions.read || errorReadingUserObject }, // Don't show this page if user doesn't have read permission on user            
            { id: GatewayGetSchema.pages.status.pageId, title: t('SMP.GATEWAYS.INNER_MENU.STATUS'), icon: gatewayStatusIcon, pageElement: GatewayStatus, helpId: helpContextPageUrl.gatewayStatus },
            { id: GatewayGetSchema.pages.internetMonitoring.pageId, title: 'Internet Monitoring', icon: internetIcon, pageElement: GatewayInternetMonitoring, helpId: helpContextPageUrl.gatewayGeneral },
            { id: 'gatewayLogs', title: t('SMP.GATEWAYS.INNER_MENU.LOGS'), icon: clipboardIcon, pageElement: GatewayLogs, helpId: helpContextPageUrl.gatewayLogs, hide: !isSMELogsEnabled },            
            { title: t('SMP.GATEWAYS.INNER_MENU.DEVICE_SETTINGS'), icon: deviceSettingsIcon, hide: isExternal, children: [
                    { id: GatewayGetSchema.pages.ntp.pageId, title: t('SMP.GATEWAYS.INNER_MENU.NTP'), icon: ntpIcon, pageElement: GatewayNtp, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.ntp.pageId), helpId: helpContextPageUrl.gatewayNtp },
                    { id: GatewayGetSchema.pages.timezone.pageId, title: t('SMP.GATEWAYS.INNER_MENU.TIME_ZONE'), icon: timezoneIcon, pageElement: GatewayTimezone, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.timezone.pageId), helpId: helpContextPageUrl.gatewayTimezone },                    
                    { id: GatewayGetSchema.pages.dns.pageId, title: t('SMP.GATEWAYS.INNER_MENU.DNS'), icon: dnsIcon, pageElement: GatewayDns, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.dns.pageId), helpId: helpContextPageUrl.gatewayDns },                    
                    { id: GatewayGetSchema.pages.administrators.pageId, title: t('SMP.GATEWAYS.INNER_MENU.ADMINISTRATORS'), icon: administratorIcon, pageElement: GatewayAdministrators, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.administrators.pageId), helpId: helpContextPageUrl.gatewayAdministrators },
                    { id: GatewayGetSchema.pages.localUsers.pageId, title: t('SMP.GATEWAYS.INNER_MENU.LOCAL_USERS'), icon: ownerIcon, pageElement: GatewayLocalUsers, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.localUsers.pageId), helpId: helpContextPageUrl.gatewayLocalUsers },
                ]
            },
            { title: t('SMP.GATEWAYS.INNER_MENU.SECURITY_SOFTWARE_BLADES'), icon: securityIcon, hide: isExternal, children: [
                    { id: GatewayGetSchema.pages.firewall.pageId, title: t('SMP.GATEWAYS.INNER_MENU.FIREWALL'), icon: firewallIcon, pageElement: GatewayFirewall, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.firewall.pageId), helpId: helpContextPageUrl.gatewayFirewall },
                    { id: GatewayGetSchema.pages.accessPolicy.pageId, title: t('SMP.GATEWAYS.INNER_MENU.ACCESS_POLICY'), icon: fwRulesIcon, pageElement: GatewayAccessPolicy, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.accessPolicy.pageId), helpId: helpContextPageUrl.gatewayAccessPolicy },
                    { id: GatewayGetSchema.pages.applicationsAndUrls.pageId, title: t('SMP.GATEWAYS.INNER_MENU.APPLICATIONS_AND_URLS'), icon: appControlIcon, pageElement: GatewayApplicationsAndUrls, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.applicationsAndUrls.pageId), helpId: helpContextPageUrl.gatewayApplicationsAndUrls },
                    { id: GatewayGetSchema.pages.ips.pageId, title: t('SMP.GATEWAYS.INNER_MENU.IPS'), icon: ipsIcon, pageElement: GatewayIps, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.ips.pageId), helpId: helpContextPageUrl.gatewayIps },
                    { id: GatewayGetSchema.pages.traditionalAntiVirus.pageId, title: t('SMP.GATEWAYS.INNER_MENU.T_ANTI_VIRUS'), icon: antiVirusIcon, pageElement: GatewayTraditionalAntiVirus, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.traditionalAntiVirus.pageId), helpId: helpContextPageUrl.gatewayTraditionalAntiVirus },
                    { id: GatewayGetSchema.pages.antiSpam.pageId, title: t('SMP.GATEWAYS.INNER_MENU.ANTI_SPAM'), icon: antiSpamIcon, pageElement: GatewayAntiSpam, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.antiSpam.pageId), helpId: helpContextPageUrl.gatewayAntiSpam },
                    { id: GatewayGetSchema.pages.qos.pageId, title: t('SMP.GATEWAYS.INNER_MENU.QOS'), icon: qosIcon, pageElement: GatewayQos, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.qos.pageId), helpId: helpContextPageUrl.gatewayQos },
                    { id: GatewayGetSchema.pages.remoteAccess.pageId, title: t('SMP.GATEWAYS.INNER_MENU.REMOTE_ACCESS'), icon: vpnRemoteIcon, pageElement: GatewayRemoteAccess, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.remoteAccess.pageId), helpId: helpContextPageUrl.gatewayRemoteAccess },
                    { id: GatewayGetSchema.pages.siteToSiteVpn.pageId, title: t('SMP.GATEWAYS.INNER_MENU.SITE_TO_SITE_VPN'), icon: vpnSiteIcon, pageElement: GatewaySiteToSiteVpn, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.siteToSiteVpn.pageId), helpId: helpContextPageUrl.gatewaySiteToSiteVpn },
                    { id: GatewayGetSchema.pages.userAwareness.pageId, title: t('SMP.GATEWAYS.INNER_MENU.USER_AWARENESS'), icon: idaIcon, pageElement: GatewayUserAwareness, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.userAwareness.pageId), helpId: helpContextPageUrl.gatewayUserAwareness },
                    { id: GatewayGetSchema.pages.antiVirus.pageId, title: t('SMP.GATEWAYS.INNER_MENU.ANTI_VIRUS'), icon: antiVirusIcon, pageElement: GatewayAntiVirus, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.antiVirus.pageId), helpId: helpContextPageUrl.gatewayAntiVirus },
                    { id: GatewayGetSchema.pages.antiBot.pageId, title: t('SMP.GATEWAYS.INNER_MENU.ANTI_BOT'), icon: antiBotIcon, pageElement: GatewayAntiBot, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.antiBot.pageId), helpId: helpContextPageUrl.gatewayAntiBot },
                    { id: GatewayGetSchema.pages.threatEmulation.pageId, title: t('SMP.GATEWAYS.INNER_MENU.THREAT_EMULATION'), icon: threatEmulationIcon, pageElement: GatewayThreatEmulation, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.threatEmulation.pageId), helpId: helpContextPageUrl.gatewayThreatEmulation },
                    { id: GatewayGetSchema.pages.threatPrevention.pageId, title: t('SMP.GATEWAYS.INNER_MENU.THREAT_PREVENTION'), icon: policyIcon, pageElement: GatewayThreatPrevention, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.threatPrevention.pageId), helpId: helpContextPageUrl.gatewayThreatPrevention },
                    { id: GatewayGetSchema.pages.unifiedThreatPrevention.pageId, title: t('SMP.GATEWAYS.INNER_MENU.UNIFIED_THREAT_PREVENTION'), icon: policyIcon, pageElement: GatewayUnifiedThreatPrevention, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.unifiedThreatPrevention.pageId), helpId: helpContextPageUrl.gatewayThreatPrevention },
                    { id: GatewayGetSchema.pages.threatPreventionExceptions.pageId, title: t('SMP.GATEWAYS.INNER_MENU.THREAT_PREVENTION_EXCEPTIONS'), icon: exceptionsIcon, pageElement: GatewayThreatPreventionExceptions, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.threatPreventionExceptions.pageId), helpId: helpContextPageUrl.gatewayThreatPreventionExceptions },
                    { id: GatewayGetSchema.pages.sslInspection.pageId, title: t('SMP.GATEWAYS.INNER_MENU.SLL_INSPECTION'), icon: sslInspectionIcon, pageElement: GatewaySSLInspection, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.sslInspection.pageId), helpId: helpContextPageUrl.gatewaySslInspection },
                ],
            },
            { title: t('SMP.PLANS.INNER_MENU.SERVICES'), icon: servicesIcon, hide: isExternal, children: [
                    { id: GatewayGetSchema.pages.managedServices.pageId, title: t('SMP.PLANS.INNER_MENU.MANAGED_SERVICES'), icon: settingsIcon, pageElement: GatewayManagedServices, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.managedServices.pageId), helpId: helpContextPageUrl.gatewayManagedServices },
                    { id: GatewayGetSchema.pages.firmware.pageId, title: t('SMP.PLANS.INNER_MENU.FIRMWARE'), icon: updatesIcon, pageElement: GatewayFirmware, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.firmware.pageId), helpId: helpContextPageUrl.gatewayFirmware },
                    { id: GatewayGetSchema.pages.serviceBackup.pageId, title: t('SMP.PLANS.INNER_MENU.PERIODIC_BACKUP'), icon: backupIcon, pageElement: GatewayPeriodicBackup, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.serviceBackup.pageId), helpId: helpContextPageUrl.gatewayServiceBackup },
                    { id: GatewayGetSchema.pages.reports.pageId, title: t('SMP.PLANS.INNER_MENU.REPORTS'), icon: reportsIcon, pageElement: GatewayReports, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.reports.pageId), helpId: helpContextPageUrl.gatewayReports },
                    { id: GatewayGetSchema.pages.reportRecipients.pageId, title: t('SMP.PLANS.INNER_MENU.REPORT_RECIPIENTS'), icon: reportsIcon, pageElement: GatewayReportRecipients, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.reportRecipients.pageId), helpId: helpContextPageUrl.gatewayReportRecipients },
                    { id: GatewayGetSchema.pages.notifications.pageId, title: t('SMP.PLANS.INNER_MENU.NOTIFICATIONS'), icon: backupIcon, pageElement: GatewayNotifications, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.notifications.pageId), helpId: helpContextPageUrl.gatewayNotifications },
                    { id: GatewayGetSchema.pages.notificationsRecipients.pageId, title: t('SMP.PLANS.INNER_MENU.NOTIFICATIONS_RECIPIENTS'), icon: reportsIcon, pageElement: GatewayNotificationsRecipients, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.notificationsRecipients.pageId), helpId: helpContextPageUrl.gatewayNotificationRecipients },
                    { id: GatewayGetSchema.pages.dynamicDns.pageId, title: t('SMP.GATEWAYS.INNER_MENU.DYNAMIC_DNS'), icon: dnsIcon, pageElement: GatewayDynamicDns, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.dynamicDns.pageId), helpId: helpContextPageUrl.gatewayDynamicDNS },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.VPN'), icon: vpnIcon, children: [
                    { id: GatewayGetSchema.pages.vpnCommunity.pageId, title: t('SMP.PLANS.INNER_MENU.COMMUNITY'), icon: communityIcon, pageElement: GatewayVpnCommunity, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.vpnCommunity.pageId), helpId: helpContextPageUrl.gatewayVpnCommunity },
                    { id: GatewayGetSchema.pages.vpnAuthenticationMethod.pageId, title: t('SMP.GATEWAYS.PAGE.VPN.AUTH_METHOD'), icon: licenseIcon, pageElement: GatewayVpnAuthenticationMethod, helpId: helpContextPageUrl.vpnAuthenticationMethod },
                    { id: GatewayGetSchema.pages.vpnInternalNetworkTopology.pageId, title: t('SMP.GATEWAYS.INNER_MENU.INTERNAL_NET_TOPOLOGY'), icon: topologyIcon, pageElement: GatewayVpnInternalNetworkTopology, helpId: helpContextPageUrl.vpnInternalNetworkTopology },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.SETUP'), icon: settingsIcon, hide: isExternal, children: [
                    { id: GatewayGetSchema.pages.administratorAccess.pageId, title: t('SMP.PLANS.INNER_MENU.ADMINISTRATORS_ACCESS'), icon: administratorAccessIcon, pageElement: GatewayAdministratorAccess, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.administratorAccess.pageId), helpId: helpContextPageUrl.administratorAccess },
                    { id: GatewayGetSchema.pages.gatewaysBehindNAT.pageId, title: t('SMP.PLANS.INNER_MENU.GATEWAYS_BEHIND_NAT'), icon: gatewaysBehindNatIcon, pageElement: GatewayGatewaysBehindNat, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.gatewaysBehindNAT.pageId), helpId: helpContextPageUrl.gatewaysBehindNAT },
                    { id: GatewayGetSchema.pages.gatewayBladesLicenses.pageId, title: 'Blades Licenses', icon: licenseIcon, pageElement: GatewayGatewayBladesLicenses, helpId: helpContextPageUrl.gatewayGeneral },
                ]
            },
            { title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPTS'), icon: scriptIcon, hide: isExternal, children: [
                    { id: GatewayGetSchema.pages.cliScript1.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_1'), icon: scriptIcon, pageElement: GatewayCliScript1, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScript1.pageId), helpId: helpContextPageUrl.cliScript },
                    { id: GatewayGetSchema.pages.cliScript2.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_2'), icon: scriptIcon, pageElement: GatewayCliScript2, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScript2.pageId), helpId: helpContextPageUrl.cliScript },
                    { id: GatewayGetSchema.pages.cliScript3.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_3'), icon: scriptIcon, pageElement: GatewayCliScript3, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScript3.pageId), helpId: helpContextPageUrl.cliScript },
                    { id: GatewayGetSchema.pages.cliScript4.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_4'), icon: scriptIcon, pageElement: GatewayCliScript4, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScript4.pageId), helpId: helpContextPageUrl.cliScript },
                    { id: GatewayGetSchema.pages.cliScript5.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_5'), icon: scriptIcon, pageElement: GatewayCliScript5, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScript5.pageId), helpId: helpContextPageUrl.cliScript },
                    { id: GatewayGetSchema.pages.cliScriptFromApi.pageId, title: t('SMP.PLANS.INNER_MENU.CLI_SCRIPT_FROM_API'), icon: scriptIcon, pageElement: GatewayCliScriptFromApi, extraIcon: getGatewayPageExtraIcon(GatewayGetSchema.pages.cliScriptFromApi.pageId), helpId: helpContextPageUrl.cliScript },
                ]
            },
            { id: GatewayGetSchema.pages.haStatus.pageId, title: t('SMP.GATEWAYS.INNER_MENU.HA_STATUS'), icon: gatewayHAStatusIcon, pageElement: GatewayHaStatus, hide: !hasHaStatusPage, helpId: helpContextPageUrl.gatewayResults },
            { id: GatewayGetSchema.pages.haMemberStatus.pageId, title: t('SMP.GATEWAYS.INNER_MENU.HA_STATUS'), icon: gatewayHAMemberStatusIcon, pageElement: GatewayHaMemberStatus, hide: !hasHaMemberStatusPage, helpId: helpContextPageUrl.gatewayResults },
            { id: GatewayGetSchema.pages.customFields.pageId, title: t('SMC_SETTINGS_CUSTOM'), icon: pageIcon, pageElement: GatewayCustomFields, hide: !hasCustomFieldPage, helpId: helpContextPageUrl.hasCustomField }
        ]
    ), [gatewayStatusIcon, gatewayHAStatusIcon, gatewayHAMemberStatusIcon, userPermissions, errorReadingUserObject, getGatewayPageExtraIcon, hasCustomFieldPage, isExternal, isSMELogsEnabled, hasHaStatusPage, hasHaMemberStatusPage, t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchGateway = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchGatewayResp = await Api.fetchSingleObject(ObjectTypes.GATEWAY, gatewayId);
            const gateway = fetchGatewayResp.data.data.gateway;
            if (!gateway) { throw new Error(); }
            setOrigGateway(gateway);
            setGatewayObject(gateway);
            setIsObjectDirty(false); // Just fetched, not dirty

            const planId = gateway[GatewayGetSchema.planId];
            const ownerId = gateway[GatewayGetSchema.ownerId];
            const fetchesToExecute = [];
            // Fetch plan if gateway has one
            planId && fetchesToExecute.push(new Promise((resolve, reject) => {
                Api.fetchSingleObject(ObjectTypes.PLAN, planId)
                .then(fetchPlanResp => {
                    if (fetchPlanResp && fetchPlanResp.data.data.plan) {
                        setOrigPlan(fetchPlanResp.data.data.plan);
                        setPlanObject(fetchPlanResp.data.data.plan);
                    } else {
                        console.error(`Error fetching plan ${planId}, response faulty`);
                        reject();
                    }
                    resolve();
                })
                .catch(error => reject(error));
            }));
            // Fetch owner if gateway has one
            ownerId && userPermissions.read && fetchesToExecute.push(new Promise((resolve) => {
                Api.fetchSingleObject(ObjectTypes.USER, ownerId)
                .then(fetchUserResp => {
                    if (fetchUserResp && fetchUserResp.data.data.user) {
                        setOrigUser(fetchUserResp.data.data.user);
                        setUserObject(fetchUserResp.data.data.user);
                    } else {
                        setErrorReadingUserObject(true)
                    }
                    resolve();
                })
                .catch(() => {
                    setErrorReadingUserObject(true)
                    resolve();
                });
            }));
            // Fetch firmwares available on this domain
            fetchesToExecute.push(new Promise((resolve) => {
                Api.getFirmwareListByVersion()
                .then(resp => {
                    setAvailableFirmwares(resp.data.data);
                    resolve();
                })
                .catch(() => {
                    console.error("Error fetching firmwares")
                    resolve();
                });
            }));
            // Get is SME reports are enabled on this domain
            fetchesToExecute.push(new Promise((resolve, reject) => {
                Api.getSessionData({
                    attributes: [
                        SessionDataGetSchema.isSMEReportEnabled,
                        SessionDataGetSchema.isReportConfirmationEnabled,
                        SessionDataGetSchema.isHasEditPermissionForReportsConfig
                    ]
                })
                .then(resp => {
                    setIsSMEReportEnabled(resp.data[SessionDataGetSchema.isSMEReportEnabled]);
                    setIsReportConfirmationEnabled(resp.data[SessionDataGetSchema.isReportConfirmationEnabled]);
                    setIsHasEditPermissionForReportsConfig(resp.data[SessionDataGetSchema.isHasEditPermissionForReportsConfig]);
                    resolve();
                })
                .catch(error => reject(error));
            }));

            // Get Zones list
            fetchesToExecute.push(new Promise((resolve) => {
                Api.getZoneList()
                .then((resp) => {
                    const zonesData = resp.data.data;
                    setZonesData(zonesData);
                    resolve();
                }) 
                .catch(() => {
                    console.error("Error fetching zones list")
                    resolve();
                });
            }));
            // Wait for all fetches to complete
            await Promise.all(fetchesToExecute);
            setIsPageLoadFailed(false);
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            if (isAxiosAuthError(e)) {
                enqueueLogoutSnackbar(errorMessage);
            } else {
                enqueueErrorSnackbar(errorMessage);
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [enqueueErrorSnackbar, getErrorMessage, enqueueLogoutSnackbar, userPermissions, gatewayId]);

    const setGatewayData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setGatewayObject(gwObj => produce(gwObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const removeGatewayOwner = useCallback(() => {
        setGatewayData([GatewayGetSchema.ownerId], null);
        setUserObject({});
    }, [setGatewayData]);

    const setGatewayOwner = useCallback((userObject) => {
        if (userObject && userObject[UserGetSchema.id]) {
            setGatewayData([GatewayGetSchema.ownerId], userObject[UserGetSchema.id]);
            setUserObject(userObject);
        } else {
            console.error('Error setting gateway owner, user object and/or user id are empty');
        }
    }, [setGatewayData]);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.GATEWAY, gatewayId, gatewayObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            // On timeout, don't set error messages
            if (!isAxiosTimeoutError(error)) {
                const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
                if (errorMessageArrayByPage) {
                    setErrorMessagesByPageList(errorMessageArrayByPage);
                } else {
                    setErrorMessagesByPageList(null);
                }
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [gatewayId, getMessagesArrayByPages, gatewayObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.GATEWAY, gatewayId, gatewayObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchGateway();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [getErrorMessage, gatewayId, fetchGateway, gatewayObject, enqueueSuccessSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setGatewayObject(origGateway);
        origPlan && setPlanObject(origPlan);
        origUser && setUserObject(origUser);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origGateway, origPlan, origUser]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [gatewayId] ))) {
            Api.deleteObjects(ObjectTypes.GATEWAY, [gatewayId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.gateways));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [gatewayId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [gatewayId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const accessGatewayButtonOnClick = useCallback(() => {
        doSeamlessLogin(gatewayId);
    }, [gatewayId]);

    const doAction = useCallback(async (actionType, actionCode) => {
        try {
            const resp = await Api.doAction(ObjectTypes.GATEWAY, actionType, [gatewayId]);
            const respData = resp.data;
            const actionResult = respData.results[0];
            if (actionResult.success) {
                enqueueSuccessSnackbar(t('SMC_GATEWAY_CMD_SENDING', [t(actionCode)]));
            } else {
                const errorMessage = t(actionResult.errorCode);
                enqueueErrorSnackbar(errorMessage);
            }
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        }
    }, [getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, gatewayId, t]);

    const rebootAction = useCallback(() => {
        if (window.confirm(t('RESULTS_OBJECTS_REBOOT_QUES'))) {
        doAction(ApiActions.gateway.reboot, 'com.sofaware.cmd.CMD_GATEWAYREBOOT');
        }
    }, [doAction, t]);

    const fetchAction = useCallback(() => {
        doAction(ApiActions.gateway.fetch, 'com.sofaware.cmd.CMD_GATEWAY_FETCH');
    }, [doAction]);

    const backupAction = useCallback(() => {
        doAction(ApiActions.gateway.backup, 'com.sofaware.cmd.CMD_GATEWAY_BACKUP');
    }, [doAction]);

    const sendDailyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendDailyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_DAILY_REPORT');
    }, [doAction]);

    const sendWeeklyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendWeeklyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_WEEKLY_REPORT');
    }, [doAction]);

    const sendMonthlyReportAction = useCallback(() => {
        doAction(ApiActions.gateway.sendMonthlyReport, 'com.sofaware.cmd.CMD_GATEWAY_SEND_MONTHLY_REPORT');
    }, [doAction]);

    const accessGatewayRMDAction = useCallback(() => {
        doSeamlessLogin(gatewayId, "reachMyDevice");
    }, [gatewayId]);

    const accessGatewayIPAction = useCallback(() => {
        doSeamlessLogin(gatewayId, "ip");
    }, [gatewayId]);

    // Fetch gateway on loading of page
    useEffect(() => {
        fetchGateway();
    }, [fetchGateway]);
        
    const reloadGatewayStatus = useCallback(() => {
        Api.fetchSingleObject(ObjectTypes.GATEWAY, gatewayId)
        .then(resp => {
            const gateway = resp.data.data.gateway;
            const statusPage = gateway[GatewayGetSchema.pages.status.pageId];
            setGatewayData(GatewayGetSchema.pages.status.pageId, statusPage);
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_REFRESHED_OK'));
        })
        .catch(() => {
            enqueueErrorSnackbar('Error reloading status page');
        })
    }, [gatewayId, setGatewayData, enqueueErrorSnackbar, enqueueSuccessSnackbar, t]);

    const reloadGatewayCliResults = useCallback(pageId => {
        Api.fetchSingleObject(ObjectTypes.GATEWAY, gatewayId)
        .then(resp => {
            const gateway = resp.data.data.gateway;
            const cliPage = gateway[pageId];
            const cliScriptsSchema = GatewayGetSchema.pages.cliScripts;
            if (cliPage) {
                if (hasValue(cliPage[cliScriptsSchema.scriptRunText])) {
                    setGatewayData([pageId, cliScriptsSchema.scriptRunText], cliPage[cliScriptsSchema.scriptRunText]);
                }
                if (hasValue(cliPage[cliScriptsSchema.scriptRunTimestamp])) {
                    setGatewayData([pageId, cliScriptsSchema.scriptRunTimestamp], cliPage[cliScriptsSchema.scriptRunTimestamp]);
                }
                if (hasValue(cliPage[cliScriptsSchema.scriptRunRC])) {
                    setGatewayData([pageId, cliScriptsSchema.scriptRunRC], cliPage[cliScriptsSchema.scriptRunRC]);
                }
            }
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_REFRESHED_OK'));
        })
        .catch(()=> {
            enqueueErrorSnackbar('Error reloading cli results');
        })
    }, [gatewayId, setGatewayData, enqueueErrorSnackbar, enqueueSuccessSnackbar, t]);

    return {
        gatewayObject,
        isObjectDirty,
        editorPages,
        helpId,
        planObject,
        userObject,
        isExternal,
        isMemberOfCluster,
        availableFirmwares,
        isSMEReportEnabled,
        isReportConfirmationEnabled,
        isHasEditPermissionForReportsConfig,
        hasIpAddress,
        isReachMyDeviceEnabled,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setGatewayData,
        setPlanObject,
        setUserObject,
        removeGatewayOwner,
        setGatewayOwner,
        saveButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchGateway,
        deleteButtonOnClick,
        accessGatewayButtonOnClick,
        rebootAction,
        fetchAction,
        backupAction,
        sendDailyReportAction,
        sendWeeklyReportAction,
        sendMonthlyReportAction,
        accessGatewayRMDAction,
        accessGatewayIPAction,
        reloadGatewayStatus,
        reloadGatewayCliResults,
        appiData,
        uoTableData,
        zonesData,
        setAppiDataValue,
        setUoTableDataValue,
    };
}